import { Camera } from '../camera';
import { InputMouseEvent } from './InputHandler';
import { DrawableObject } from '../renderer';
import { Engine } from '../Engine';
import { DefaultPicker } from './DefaultPicker';
import { Ray } from '../math';

export interface Picker {
  pick(camera: Camera, x: number, y: number): DrawableObject | undefined;
}

export class MouseObjectInteraction {
  private lastMouseMoveEvent?: InputMouseEvent;
  private hoverDirty = false;
  private currentHoverObject?: DrawableObject;
  private picker: Picker;

  constructor(private engine: Engine) {
    // Hover picking is throttled to once per 50ms. The dirty flag ensures
    // we only re-pick when the mouse has actually moved since the last
    // pick — without it, the timer fires forever after the first move,
    // burning a full picking render pass + readPixels stall every 50ms.
    this.engine.tickEventManager.registerTimeEvent(() => {
      if (this.hoverDirty && this.lastMouseMoveEvent) {
        this.objectHover(this.lastMouseMoveEvent);
        this.hoverDirty = false;
      }
    }, 50);

    this.picker = new DefaultPicker(this.engine);
  }

  private objectHover(e: InputMouseEvent) {
    if (!this.engine.activeScene) return;

    let x = e.position.x;
    let y = e.position.y;

    let obj = this.picker.pick(this.engine.activeCamera, x, y);

    if (obj && obj.interaction) {
      // @ts-ignore
      obj.interaction?.onHoverIn();
    } else {
      let hitMap = this.engine.activeScene.pickingManager.getRayIntersections(this.getRay(e));
      hitMap.forEach((value, key) => {
        value.callback(key, 'mouseMove', value.hits);
      });
    }

    if (this.currentHoverObject && this.currentHoverObject !== obj) {
      // @ts-ignore
      this.currentHoverObject.interaction?.onHoverOut();
    }

    if (obj && obj.interaction) {
      this.currentHoverObject = obj;
    }
  }

  private getRay(e: InputMouseEvent): Ray {
    return this.engine.activeCamera.getRay(e.position.x, e.position.y);
  }

  public mouseMoveEvent(e: InputMouseEvent) {
    this.lastMouseMoveEvent = e;
    this.hoverDirty = true;
  }

  public mouseUpEvent(e: InputMouseEvent) {
    if (!this.engine.activeScene) return;

    let obj = this.picker.pick(this.engine.activeCamera, e.position.x, e.position.y);

    if (obj && obj.interaction) {
      // @ts-ignore
      obj.interaction?.onPressRelease();
    } else {
      let hitMap = this.engine.activeScene.pickingManager.getRayIntersections(this.getRay(e));
      let done = false;

      hitMap.forEach((value, key) => {
        if (!done) {
          // TODO: This feels dirty, is there a different way to loop over Sets/Maps?
          done = value.callback(key, 'mouseUp', value.hits);
        }
      });
    }
  }

  public mouseDownEvent(e: InputMouseEvent) {
    if (!this.engine.activeScene) return;

    let obj = this.picker.pick(this.engine.activeCamera, e.position.x, e.position.y);

    if (obj && obj.interaction) {
      // @ts-ignore
      obj.interaction?.onPressDown();
    } else {
      let hitMap = this.engine.activeScene.pickingManager.getRayIntersections(this.getRay(e));
      hitMap.forEach((value, key) => {
        value.callback(key, 'mouseDown', value.hits);
      });
    }
  }
}
